import React, { useState, useEffect } from 'react';
import { Row, Col, Card, ProgressBar, Spinner, Badge } from 'react-bootstrap';
import { useAuth } from '../../../contexts/AuthContext';
import { FaLock, FaMedal, FaStar, FaChevronRight } from 'react-icons/fa';

export default function TrustLevelProgress() {
  const { currentUser, token } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        setLoading(true);
        const res = await fetch(`${process.env.REACT_APP_BASE_URL || ''}/api/community/profile/${currentUser.uid}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const data = await res.json();
        if (data.success) {
          setProfile(data.profile);
        }
      } catch (err) {
        console.error("Error fetching profile", err);
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
  }, [currentUser, token]);

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center py-5">
        <Spinner animation="border" variant="warning" />
      </div>
    );
  }

  const reputation = profile?.reputation || 0;
  
  // Calculate level based on reputation (gamification logic)
  const calculateLevel = (rep) => {
    if (rep >= 1000) return { level: 'Platinum', next: null, progress: 100 };
    if (rep >= 500) return { level: 'Gold', next: 1000, progress: (rep - 500) / 500 * 100 };
    if (rep >= 100) return { level: 'Silver', next: 500, progress: (rep - 100) / 400 * 100 };
    return { level: 'Bronze', next: 100, progress: rep / 100 * 100 };
  };

  const { level, next, progress } = calculateLevel(reputation);
  const badges = profile?.badges || {};
  const badgeKeys = Object.keys(badges);

  return (
    <div className="py-4 community-dashboard-container">
      <h2 className="mb-4">Trust & Badges</h2>
      
      <Row className="mb-4">
        <Col lg={8}>
          <Card className="shadow-sm border-0 h-100">
            <Card.Body className="p-4">
              <div className="d-flex justify-content-between align-items-end mb-3">
                <div>
                  <h4 className="mb-1 text-warning fw-bold">{level} Trust Level</h4>
                  <p className="text-muted mb-0">Current Reputation: {reputation} XP</p>
                </div>
                {next && (
                  <div className="text-end">
                    <p className="text-muted small mb-1">Next Level: {calculateLevel(next).level}</p>
                    <p className="fw-bold mb-0">{next - reputation} XP to go</p>
                  </div>
                )}
              </div>
              
              <ProgressBar 
                now={progress} 
                variant="warning" 
                style={{ height: '12px' }} 
                className="mb-4 rounded-pill"
              />

              <h5 className="mb-3 mt-5">How to earn Reputation (XP)</h5>
              <div className="d-flex flex-column gap-3">
                <div className="d-flex align-items-center justify-content-between border-bottom pb-2">
                  <span>Getting an upvote on your discussion</span>
                  <Badge bg="success">+5 XP</Badge>
                </div>
                <div className="d-flex align-items-center justify-content-between border-bottom pb-2">
                  <span>Getting an upvote on your reply</span>
                  <Badge bg="success">+2 XP</Badge>
                </div>
                <div className="d-flex align-items-center justify-content-between border-bottom pb-2">
                  <span>Having a post approved by moderators</span>
                  <Badge bg="success">+10 XP</Badge>
                </div>
                <div className="d-flex align-items-center justify-content-between">
                  <span>Completing a successful rental on BBR</span>
                  <Badge bg="success">+50 XP</Badge>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={4}>
          <Card className="shadow-sm border-0 h-100">
            <Card.Body className="p-4">
              <h5 className="mb-4">Your Badges ({badgeKeys.length})</h5>
              
              {badgeKeys.length > 0 ? (
                <div className="d-flex flex-wrap gap-2">
                  {Object.values(badges).map((badge, idx) => (
                    <Badge key={idx} bg="dark" className="p-2 border border-secondary text-light w-100 d-flex align-items-center">
                      <FaMedal className="text-warning me-2" size={16} /> 
                      <span className="flex-grow-1 text-start">{badge.name}</span>
                    </Badge>
                  ))}
                </div>
              ) : (
                <div className="text-center py-4 bg-light rounded border border-dashed">
                  <FaMedal size={30} className="text-secondary mb-2" />
                  <p className="text-muted small mb-0">You haven't earned any badges yet.</p>
                </div>
              )}

              <h6 className="mt-5 mb-3">Locked Badges</h6>
              <div className="d-flex flex-column gap-2 opacity-50">
                <Badge bg="light" text="dark" className="p-2 border w-100 d-flex align-items-center">
                  <FaLock className="me-2 text-muted" size={14} /> 
                  <span className="flex-grow-1 text-start">Conversation Starter (Create 10 Threads)</span>
                </Badge>
                <Badge bg="light" text="dark" className="p-2 border w-100 d-flex align-items-center">
                  <FaLock className="me-2 text-muted" size={14} /> 
                  <span className="flex-grow-1 text-start">Helpful Guide (Receive 50 Upvotes)</span>
                </Badge>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
}
